import { useState, useEffect, useRef } from "react";
import { useDailyReport, useReportExportCases, useReportSearch } from "../../../../hooks/useReportQueries";
import { useClients } from '../../../../hooks/useWorkshopQueries';
import {XlsxIcon} from "../ReportsPage";

const SEARCH_TYPES = [
  { value: "client",   label: "Client",   icon: "🏢", placeholder: "Search by client name…" },
  { value: "vehicle",  label: "Vehicle",  icon: "🚛", placeholder: "Search by vehicle number…" },
  { value: "customer", label: "Customer", icon: "👤", placeholder: "Search by customer name…" },
];

function DailyReportTab({ reportSearch, setReportSearch, reportFilters, setReportFilters, onExportExcel, exporting }) {
   const [searchType, setSearchType] = useState("client"); // default
    const [searchInput, setSearchInput] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const debounceRef = useRef(null);

    const { data: clients = [], isLoading: clientsLoading } = useClients();

  // Debounce search
  useEffect(() => {
    if (searchType === "client") return; // client uses selector, not text search
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setReportSearch(searchInput);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput,searchType, setReportSearch]);
 
  const { data: searchResults = [], isLoading: searching } = useReportSearch( searchType !== "client" ? reportSearch : "", searchType);
  console.log("Search results:", searchResults);
 
  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

    const handleTypeChange = (type) => {
    setSearchType(type);
    setSearchInput("");
    setReportSearch("");
    setShowDropdown(false);
    setReportFilters(null);
    };

     // ── Client selected from the dropdown select ───────────────────────────────
  const selectClient = (client) => {
    if (!client) {
      clearSelection();
      return;
    }
    setReportFilters({
      clientId:   client.id,
      clientName: client.clientName,
    });
    setSearchInput(client.clientName);
  };

  const selectResult = (result) => {
    const filters = {};
   if (result.matchType === "vehicle")  {
      filters.vehicleNumber = result.vehicleNumber;
      filters.clientId      = result.clientId   || null;
      filters.clientName    = result.clientName || null;
    }
    if (result.matchType === "customer") {
      filters.customerName = result.customerName;
      filters.clientId     = result.clientId   || null;
      filters.clientName   = result.clientName || null;
    }

    setReportFilters(filters);
    setSearchInput(result.displayLabel  || result.vehicleNumber || result.customerName);
    setShowDropdown(false);
  };
 
  const clearSelection = () => {
    setReportFilters(null);
    setSearchInput("");
    setReportSearch("");
  };
 
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
 
      {/* ── Intro card ── */}
      <div className="wd-card" style={{ padding:20, borderLeft:"3px solid #3b82f6", display:"flex", gap:20, alignItems:"flex-start" }}>
        <div style={{ fontSize:32 }}>📋</div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:15, fontWeight:700, color:"var(--wd-t1)", marginBottom:4 }}>Daily Status Report Generator</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", lineHeight:1.6 }}>
            Select a <strong>client</strong> from the dropdown, or search by <strong>vehicle number</strong> / <strong>customer name</strong>.
            Once selected, click <strong>Export Excel</strong> to download the formatted Daily Status Report.
          </div>
        </div>
      </div>
 
      {/* ── Search box ── */}
      <div className="wd-card" style={{ padding:20 }}>
        <div style={{ fontSize:13, fontWeight:600, color:"var(--wd-t1)", marginBottom:12 }}>Search Cases</div>
 
        <div style={{ position:"relative", maxWidth:520 }} ref={dropdownRef}>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {/* Type selector */}
            <div style={{ display:"flex", border:"1px solid var(--wd-border)", borderRadius:8, overflow:"hidden", flexShrink:0 }}>
                {SEARCH_TYPES.map(t => (
                <button
                    key={t.value}
                    onClick={() => { setSearchType(t.value); setSearchInput(""); setReportSearch(""); setShowDropdown(false); }}
                    style={{
                    padding:"8px 14px",
                    background: searchType === t.value ? "var(--wd-blue)" : "var(--wd-card)",
                    color: searchType === t.value ? "#fff" : "var(--wd-t2)",
                    border:"none", cursor:"pointer",
                    fontSize:12, fontWeight:600,
                    display:"flex", alignItems:"center", gap:5,
                    borderRight:"1px solid var(--wd-border)",
                    transition:"background .15s",
                    }}
                >
                    <span>{t.icon}</span> {t.label}
                </button>
                ))}
      </div>

       {/* ── Client selector (shown only when type = client) ── */}
          {searchType === "client" && (
            <div style={{ flex:1, minWidth:220, position:"relative" }}>
              <select
                className="wd-input wd-select"
                style={{ width:"100%", height:38, paddingLeft:12 }}
                value={reportFilters?.clientId || ""}
                onChange={e => {
                  const selected = clients.find(c => String(c.id) === String(e.target.value));
                  selectClient(selected || null);
                }}
                disabled={clientsLoading}
              >
                <option value="">
                  {clientsLoading ? "Loading clients…" : "— Select a client —"}
                </option>
                {clients
                  .filter(c => c.isActive)
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.clientName}
                      {c.clientShort ? ` (${c.clientShort})` : ""}
                    </option>
                  ))
                }
                {clients.some(c => !c.isActive) && (
                  <>
                    <option disabled>──────────────</option>
                    <option disabled style={{ color:"var(--wd-t3)", fontSize:11 }}>Inactive clients</option>
                    {clients
                      .filter(c => !c.isActive)
                      .map(c => (
                        <option key={c.id} value={c.id} style={{ color:"var(--wd-t3)" }}>
                          {c.clientName}
                          {c.clientShort ? ` (${c.clientShort})` : ""} — Inactive
                        </option>
                      ))
                    }
                  </>
                )}
              </select>
            </div>
          )}
          
          {/* ── Vehicle / Customer text search ── */}
          {searchType !== "client" && (
            <div style={{ flex:1, minWidth:220, position:"relative" }} ref={dropdownRef}>
              <input
                className="wd-input"
                style={{ width:"100%", paddingLeft:36, paddingRight: reportFilters ? 32 : 12, height:38 }}
                placeholder={SEARCH_TYPES.find(t => t.value === searchType)?.placeholder}
                value={searchInput}
                onChange={e => { setSearchInput(e.target.value); setShowDropdown(true); }}
                onFocus={() => searchResults.length && setShowDropdown(true)}
              />
              <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", fontSize:14 }}>🔍</span>
              {searching && (
                <span style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", fontSize:11, color:"var(--wd-t3)" }}>…</span>
              )}
              {reportFilters && !searching && (
                <button
                  onClick={clearSelection}
                  style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:14, color:"var(--wd-t3)", lineHeight:1 }}
                  title="Clear selection"
                >✕</button>
              )}
 
              {/* Search dropdown */}
              {showDropdown && searchResults.length > 0 && (
                <div style={{
                  position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:100,
                  background:"var(--wd-card)", border:"1px solid var(--wd-border)",
                  borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,.15)", overflow:"hidden",
                }}>
                  {searchResults.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => selectResult(r)}
                      style={{
                        width:"100%", textAlign:"left", padding:"10px 14px", background:"none",
                        border:"none", borderBottom:"1px solid var(--wd-border)", cursor:"pointer",
                        display:"flex", alignItems:"center", gap:10,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--wd-hover)"}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}
                    >
                      <span style={{ fontSize:16 }}>{r.matchType === "vehicle" ? "🚛" : "👤"}</span>
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color:"var(--wd-t1)" }}>
                          {r.displayLabel || r.vehicleNumber || r.customerName}
                        </div>
                        <div style={{ fontSize:10, color:"var(--wd-t3)" }}>
                          {r.matchType === "vehicle"  && `Vehicle · ${r.clientName || "—"}`}
                          {r.matchType === "customer" && `Customer · ${r.clientName || "—"}`}
                        </div>
                      </div>
                      <div style={{ marginLeft:"auto", fontSize:10, background:"var(--wd-hover)", padding:"2px 8px", borderRadius:10, color:"var(--wd-t3)", textTransform:"capitalize" }}>
                        {r.matchType}
                      </div>
                    </button>
                  ))}
                </div>
              )}
 
              {showDropdown && reportSearch.length >= 2 && searchResults.length === 0 && !searching && (
                <div style={{
                  position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:100,
                  background:"var(--wd-card)", border:"1px solid var(--wd-border)",
                  borderRadius:8, padding:"14px 16px", textAlign:"center",
                  color:"var(--wd-t3)", fontSize:12,
                }}>
                  No results found for "{reportSearch}"
                </div>
              )}
            </div>
          )}
 
            {reportFilters && (
              <button
                className="wd-btn wd-btn-primary"
                onClick={onExportExcel}
                disabled={exporting}
                style={{ whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:6 }}
              >
                <XlsxIcon />
                {exporting ? "Generating…" : "Export Excel"}
              </button>
            )}
          </div>

          {/* Search dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <div style={{
              position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:100,
              background:"var(--wd-card)", border:"1px solid var(--wd-border)",
              borderRadius:8, boxShadow:"0 8px 24px rgba(0,0,0,.15)", overflow:"hidden",
            }}>
              {searchResults.map((r, i) => (
                <button
                  key={i}
                  onClick={() => selectResult(r)}
                  style={{
                    width:"100%", textAlign:"left", padding:"10px 14px", background:"none",
                    border:"none", borderBottom:"1px solid var(--wd-border)", cursor:"pointer",
                    display:"flex", alignItems:"center", gap:10,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--wd-hover)"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <span style={{ fontSize:16 }}>{r.matchType === "client" ? "🏢" : r.matchType === "vehicle" ? "🚛" : "👤"}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:"var(--wd-t1)" }}>
                      {r.displayLabel || r.clientName || r.vehicleNumber || r.customerName}
                    </div>
                    <div style={{ fontSize:10, color:"var(--wd-t3)" }}>
                      {r.matchType === "client"   && `Client · ${r.caseCount ?? "?"} cases`}
                      {r.matchType === "vehicle"  && `Vehicle · ${r.clientName || ""}`}
                      {r.matchType === "customer" && `Customer · ${r.clientName || ""}`}
                    </div>
                  </div>
                  <div style={{ marginLeft:"auto", fontSize:10, background:"var(--wd-hover)", padding:"2px 8px", borderRadius:10, color:"var(--wd-t3)", textTransform:"capitalize" }}>
                    {r.matchType}
                  </div>
                </button>
              ))}
            </div>
          )}
 
          {showDropdown && reportSearch.length >= 2 && searchResults.length === 0 && !searching && (
            <div style={{
              position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:100,
              background:"var(--wd-card)", border:"1px solid var(--wd-border)",
              borderRadius:8, padding:"14px 16px", textAlign:"center",
              color:"var(--wd-t3)", fontSize:12,
            }}>
              No results found for "{reportSearch}"
            </div>
          )}
        </div>
      </div>
 
      {/* ── Selected filters info ── */}
      {reportFilters && (
        <div className="wd-card" style={{ padding:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:"var(--wd-t1)", marginBottom:2 }}>Selected Filter</div>
              <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Cases matching this filter will be included in the Excel report</div>
            </div>
            <button className="wd-btn wd-btn-ghost" onClick={clearSelection} style={{ fontSize:12 }}>Clear</button>
          </div>
 
          <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
            {reportFilters.clientName && (
              <FilterChip icon="🏢" label="Client" value={reportFilters.clientName} color="#3b82f6" />
            )}
            {reportFilters.vehicleNumber && (
              <FilterChip icon="🚛" label="Vehicle" value={reportFilters.vehicleNumber} color="#f97316" />
            )}
            {reportFilters.customerName && (
              <FilterChip icon="👤" label="Customer" value={reportFilters.customerName} color="#a855f7" />
            )}
          </div>
 
          {/* Preview of what'll be in the report */}
          <DailyReportPreview filters={reportFilters} />
        </div>
      )}
 
       {/* ── Empty state ── */}
      {!reportFilters && (
        <div className="wd-card" style={{ padding:40, textAlign:"center" }}>
          <div style={{ fontSize:48, marginBottom:12 }}>📊</div>
          <div style={{ fontSize:14, fontWeight:600, color:"var(--wd-t2)", marginBottom:8 }}>No filter selected</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)" }}>
            Select a client from the dropdown, or search by vehicle number / customer name — then export their daily status report as Excel.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filter chip ──────────────────────────────────────────────────────────────
function FilterChip({ icon, label, value, color }) {
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:8, padding:"8px 14px",
      borderRadius:20, border:`1.5px solid ${color}22`, background:`${color}11`,
    }}>
      <span style={{ fontSize:14 }}>{icon}</span>
      <div>
        <div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".06em", color, fontWeight:700 }}>{label}</div>
        <div style={{ fontSize:12, fontWeight:600, color:"var(--wd-t1)" }}>{value}</div>
      </div>
    </div>
  );
}

// ─── Daily report preview (uses existing useDailyReport hook) ─────────────────
function DailyReportPreview({ filters }) {
    const [status,   setStatus]   = useState("ALL");
    const [category, setCategory] = useState("ALL");
    const searchTerm = filters.vehicleNumber || filters.customerName || filters.clientName || "";
    const searchType = filters.vehicleNumber  ? "vehicle": filters.customerName   ? "customer": "client";

    const { data, isLoading } = useDailyReport({
      range:    "Last 12 months",
      status,
      category,
      clientId: filters.clientId  || null,   // ← preferred fast path
      search:   filters.clientId  ? null : searchTerm,
      type:     filters.clientId  ? null : searchType,
    });
 
    const summary   = data?.pages?.[0]?.summary;
    const rows      = data?.pages?.flatMap(p => p.data) ?? [];  // ← add this
    console.log("Daily report preview data:", { summary, rows });
    const total     = summary?.total     ?? rows.length;
    const settled   = summary?.settled   ?? rows.filter(r => /settled/i.test(r.autoStatus)).length;
    const inProcess = summary?.active    ?? rows.filter(r => !/settled/i.test(r.autoStatus)).length;
    const overdue   = summary?.overdue   ?? rows.filter(r => r.daysLapsed > 120).length;
 
  if (isLoading) {
    return <div style={{ padding:"20px 0", textAlign:"center", color:"var(--wd-t3)", fontSize:12 }}>Loading preview…</div>;
  }
 
  return (
    <div style={{ marginTop:20 }}>

      {/* ── Filter bar ── */}
      <div style={{ display:"flex", gap:10, marginBottom:16, alignItems:"center" }}>
        
        {/* Status filter */}
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600 }}>STATUS</span>
          <div style={{ display:"flex", border:"1px solid var(--wd-border)", borderRadius:6, overflow:"hidden" }}>
            {["ALL","ACTIVE","SETTLED"].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                style={{
                  padding:"5px 10px", fontSize:11, fontWeight:600, border:"none", cursor:"pointer",
                  borderRight:"1px solid var(--wd-border)",
                  background: status === s ? "var(--wd-blue)" : "var(--wd-card)",
                  color:      status === s ? "#fff" : "var(--wd-t3)",
                }}
              >{s}</button>
            ))}
          </div>
        </div>

        {/* Category filter */}
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600 }}>TYPE</span>
          <div style={{ display:"flex", border:"1px solid var(--wd-border)", borderRadius:6, overflow:"hidden" }}>
            {["ALL","MAJOR","MINOR","AVERAGE"].map(c => (
              <button key={c} onClick={() => setCategory(c)}
                style={{
                  padding:"5px 10px", fontSize:11, fontWeight:600, border:"none", cursor:"pointer",
                  borderRight:"1px solid var(--wd-border)",
                  background: category === c ? "var(--wd-blue)" : "var(--wd-card)",
                  color:      category === c ? "#fff" : "var(--wd-t3)",
                }}
              >{c}</button>
            ))}
          </div>
        </div>

        <span style={{ marginLeft:"auto", fontSize:11, color:"var(--wd-t3)" }}>
          {total} cases match
        </span>
      </div>

      {/* Summary row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
        {[
          { label:"Total Cases",   value:total,    color:"#3b82f6" },
          { label:"Claim Settled", value:settled,  color:"#22c55e" },
          { label:"In Process",    value:inProcess,color:"#f97316" },
          { label:"Overdue >120d", value:overdue,  color:"#ef4444" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ padding:"10px 14px", borderRadius:8, borderLeft:`3px solid ${color}`, background:`${color}11` }}>
            <div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".06em", color:"var(--wd-t3)", fontWeight:600, marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:800, color }}>{value}</div>
          </div>
        ))}
      </div>
 
      {/* Table preview */}
      {rows.length > 0 ? (
        <div>
          <div style={{ fontSize:11, color:"var(--wd-t3)", marginBottom:8, fontWeight:600 }}>PREVIEW ({Math.min(rows.length, 5)} of {total} cases)</div>
          <div className="wd-table-wrap">
            <table className="wd-table">
              <thead>
                <tr>
                  <th>SR</th>
                  <th>VEHICLE NO.</th>
                  <th>ACCIDENT CASE NO.</th>
                  <th>ACCIDENT TYPE</th>
                  <th>DATE OF ACCIDENT</th>
                  <th>WORKSHOP</th>
                  <th>DAYS LAPSED</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 5).map((r, i) => (
                  <tr key={r.vehicleNumber + i}>
                    <td style={{ color:"var(--wd-t3)", fontWeight:600 }}>{i + 1}</td>
                    <td style={{ fontWeight:700, fontFamily:"var(--wd-mono)" }}>{r.vehicleNumber || "—"}</td>
                    <td style={{ fontWeight:700, fontFamily:"var(--wd-mono)" }}>{r.accidentCaseNo || "—"}</td>
                    <td><CategoryBadge cat={r.category} /></td>
                    <td style={{ color:"var(--wd-t2)", fontSize:11 }}>
                      {r.dateOfAccident ? new Date(r.dateOfAccident).toLocaleDateString("en-IN") : "—"}
                    </td>
                    <td style={{ fontSize:11 }}>{r.workshopName || "—"}</td>
                    <td>
                      <span style={{ fontWeight:700, color: r.daysLapsed > 120 ? "var(--wd-red)" : r.daysLapsed > 60 ? "var(--wd-orange)" : "var(--wd-green)" }}>
                        {r.daysLapsed > 0 ? `${r.daysLapsed}d` : "—"}
                      </span>
                    </td>
                    <td><AutoStatusBadge status={r.autoStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 5 && (
            <div style={{ textAlign:"center", padding:"8px 0", fontSize:11, color:"var(--wd-t3)" }}>
              + {rows.length - 5} more cases will be included in the Excel report
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign:"center", padding:20, color:"var(--wd-t3)", fontSize:12 }}>
          No cases found matching the selected filter.
        </div>
      )}
    </div>
  );
}

function CategoryBadge({ cat = "" }) {
  const c = cat.toUpperCase();
  const color = c === "MAJOR" ? "#ef4444" : c === "MINOR" ? "#22c55e" : "#f97316";
  return (
    <span style={{ fontSize:10, fontWeight:700, color, background:`${color}15`, padding:"2px 8px", borderRadius:10 }}>
      {cat.toUpperCase()}
    </span>
  );
}
 
function AutoStatusBadge({ status = "" }) {
  const s = status.toUpperCase();
  const map = {
    SETTLED:    { label:"✅ Settled",     color:"#22c55e" },
    CRITICAL:   { label:"🔴 Critical",    color:"#ef4444" },
    OVERDUE:    { label:"🟡 Overdue",     color:"#eab308" },
    ACTIVE:     { label:"🔵 In Progress", color:"#3b82f6" },
  };
  const found = Object.entries(map).find(([k]) => s.includes(k));
  const { label, color } = found?.[1] || { label: status, color:"#94a3b8" };
  return <span style={{ fontSize:10, fontWeight:600, color }}>{label}</span>;
}

export default DailyReportTab;
